import type { HelpArticle } from '../../../components/help/types';

export const managingRecordsArticles: HelpArticle[] = [
  {
    id: 'records-overview',
    title: 'Records Management Overview',
    summary: 'Learn the fundamentals of managing records in OHCS RMS.',
    category: 'records',
    order: 1,
    keywords: ['records', 'documents', 'files', 'management', 'organization'],
    content: `
# Records Management Overview

The Records module is your central repository for all institutional documents.

## Key Concepts

### Documents
Documents are files stored in the system with metadata including:
- Title and description
- Document type (Letter, Memo, Report, etc.)
- Classification level
- Status (Draft, Pending, Approved, etc.)
- Version history

### Folders
Folders help organize documents:
- Create nested folder structures
- Set access permissions per folder
- Mirror your departmental structure

### Classifications
Documents have security classifications:
- **Unclassified**: Open access
- **Restricted**: Limited access
- **Confidential**: Sensitive information
- **Secret**: Highly sensitive
- **Top Secret**: Maximum security

## Common Tasks

1. Upload new documents
2. Create and organize folders
3. Search for documents
4. View document history
5. Download documents
    `,
  },
  {
    id: 'records-upload',
    title: 'Uploading Documents',
    summary: 'How to upload documents to the system.',
    category: 'records',
    order: 2,
    keywords: ['upload', 'add', 'new', 'document', 'file', 'create'],
    content: `
# Uploading Documents

Add new documents to the system quickly and efficiently.

## Upload Methods

### Single File Upload
1. Navigate to the destination folder
2. Click the **Upload** button
3. Select your file
4. Fill in the document details
5. Click **Save**

### Drag and Drop
1. Open the destination folder
2. Drag files from your computer
3. Drop them in the document area
4. Complete the metadata form

### Bulk Upload
1. Click **Upload Multiple**
2. Select multiple files
3. Apply common metadata
4. Adjust individual details if needed
5. Click **Upload All**

## Required Information

- **Title**: A clear, descriptive name
- **Document Type**: Select the appropriate category
- **Classification**: Set the security level
- **Description**: Optional but recommended

## Supported Formats

- Documents: PDF, DOC, DOCX, ODT
- Spreadsheets: XLS, XLSX, CSV
- Images: PNG, JPG, JPEG, GIF
- Other: TXT, RTF, HTML

## Best Practices

- Use descriptive, consistent naming
- Set appropriate classification levels
- Add relevant keywords for searchability
- Include a description for context
    `,
  },
  {
    id: 'records-versioning',
    title: 'Document Versioning',
    summary: 'Understanding and using document version control.',
    category: 'records',
    order: 3,
    keywords: ['version', 'history', 'revision', 'update', 'changes', 'track'],
    content: `
# Document Versioning

Track changes and maintain document history with versioning.

## How Versioning Works

Every time a document is updated, a new version is created:
- Version numbers increment automatically
- Previous versions are preserved
- All changes are tracked with timestamps

## Viewing Version History

1. Open the document
2. Click the **History** tab
3. See all versions with:
   - Version number
   - Date modified
   - Modified by (user name)
   - Change notes

## Comparing Versions

1. Select two versions to compare
2. Click **Compare**
3. View differences highlighted

## Restoring Previous Versions

1. Find the version you want
2. Click **Restore**
3. Confirm the action
4. A new version is created with the old content

## Best Practices

- Add meaningful change notes when updating
- Review history before making major changes
- Use restore carefully - it creates a new version
- Keep version notes concise but informative
    `,
  },
];
